import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { Bell, Music, Video, Check, CheckCheck, User, Heart, Share2, Users, ListMusic, MessageSquare, DollarSign, Bot, ShieldCheck, Trophy, AtSign, Radio, FileCheck, FileX, Ticket } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NotificationBell = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { liveNotifications, unreadNotificationCount, clearUnreadNotifications } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Merge live notifications with fetched ones
  useEffect(() => {
    if (liveNotifications.length > 0) {
      setNotifications(prev => {
        // Merge live notifications at the top, avoiding duplicates
        const liveIds = new Set(liveNotifications.map(n => n.id));
        const filtered = prev.filter(n => !liveIds.has(n.id));
        return [...liveNotifications, ...filtered].slice(0, 20);
      });
    }
  }, [liveNotifications]);

  // Update unread count when WebSocket notifications arrive
  useEffect(() => {
    setUnreadCount(prev => prev + unreadNotificationCount);
  }, [unreadNotificationCount]);

  useEffect(() => {
    fetchUnreadCount();
    // iter_474 — Skip polling while tab hidden (battery/heat fix).
    // WebSocket handles real-time deltas so the 60s poll is only a
    // safety net while focused.
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchUnreadCount();
    }, 60000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchUnreadCount(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [token]);

  const fetchUnreadCount = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const fetchNotifications = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.slice(0, 10)); // Show last 10
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      clearUnreadNotifications(); // Clear WebSocket unread count
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // NEW: prefer the explicit `link` field set by the centralized
    // notify() helper / type registry. Falls back to the legacy switch
    // below for older notification rows that don't yet carry it.
    if (notification.link && typeof notification.link === 'string' && notification.link.startsWith('/')) {
      navigate(notification.link);
      return;
    }

    // Determine redirect based on notification type and link info
    const notifType = notification.notification_type || notification.type;
    const linkType = notification.link_type || notification.content_type;
    const linkId = notification.link_id || notification.content_id;
    
    // Handle specific notification types first
    switch (notifType) {
      case 'viewed_profile':
      case 'profile_view':
        // Go to the viewer's profile
        navigate(`/profile/${notification.from_user_id}`);
        return;
      case 'follow':
      case 'new_friend':
        // Go to the follower's profile
        navigate(`/profile/${notification.from_user_id}`);
        return;
      case 'like':
      case 'track_like':
        // Go to the liked track
        if (notification.content_id || notification.track_id) {
          navigate(`/track/${notification.content_id || notification.track_id}`);
        }
        return;
      case 'beat_like':
        // Go to the liked beat
        if (notification.content_id || notification.beat_id) {
          navigate(`/beat/${notification.content_id || notification.beat_id}`);
        }
        return;
      case 'video_like':
        // Go to the liked video
        if (notification.content_id || notification.video_id) {
          navigate(`/free-videos?video=${notification.content_id || notification.video_id}`);
        }
        return;
      case 'comment':
      case 'track_comment':
        // Go to the track with the comment
        if (notification.content_id || notification.track_id) {
          navigate(`/track/${notification.content_id || notification.track_id}?showComments=true`);
        }
        return;
      case 'beat_comment':
        // Go to the beat with the comment
        if (notification.content_id || notification.beat_id) {
          navigate(`/beat/${notification.content_id || notification.beat_id}?showComments=true`);
        }
        return;
      case 'video_comment':
        // Go to the video with the comment
        if (notification.content_id || notification.video_id) {
          navigate(`/free-videos?video=${notification.content_id || notification.video_id}&showComments=true`);
        }
        return;
      case 'leaderboard_achievement':
        // Go to leaderboard page
        navigate('/leaderboard');
        return;
      case 'donation':
      case 'merch_sale':
        // Go to earnings/revenue
        navigate('/dashboard?tab=earnings');
        return;
      case 'livestream':
        // Open livestream URL in new tab
        if (notification.stream_url) {
          window.open(notification.stream_url, '_blank');
        } else {
          navigate(`/profile/${notification.from_user_id}`);
        }
        return;
      case 'live_radio':
        // Go to homepage to watch live radio
        navigate('/home');
        return;
      case 'music_review_submission':
      case 'music_review_priority':
      case 'music_review_spotlight':
      case 'music_review':
        // Navigate to the review session
        if (notification.session_code) {
          navigate(`/review/${notification.session_code}`);
        } else if (notification.session_id) {
          navigate(`/music-review`);
        } else {
          navigate('/music-review');
        }
        return;
      case 'reviewer_application_approved':
      case 'reviewer_application_denied':
        navigate('/music-review');
        return;
      case 'video_submission':
        // Navigate to the review session Videos tab
        if (notification.data?.session_code) {
          navigate(`/review/${notification.data.session_code}?tab=videos`);
        } else if (notification.session_code) {
          navigate(`/review/${notification.session_code}?tab=videos`);
        } else {
          navigate('/music-review');
        }
        return;
      case 'new_payout_request':
      case 'payout_request':
        // Admin notification - navigate to admin payouts tab
        navigate('/admin?tab=payouts');
        return;
      case 'new_ai_verification_request':
        // Admin notification - navigate to admin AI verification review
        navigate('/admin?tab=verifications');
        return;
      case 'ai_verification_approved':
        // User notification - navigate to settings/verification
        navigate('/settings');
        return;
      case 'ai_verification_rejected':
        // User notification - navigate to settings/verification to resubmit
        navigate('/settings');
        return;
      case 'new_upload':
        // Navigate to the uploaded content
        if (notification.content_type === 'video') {
          navigate(`/videos/${notification.content_id}`);
        } else {
          navigate(`/track/${notification.content_id}`);
        }
        return;
      case 'referral_success':
        navigate('/dashboard');
        return;
      case 'pre_save':
      case 'release_live':
        if (notification.content_id) {
          navigate(`/track/${notification.content_id}`);
        }
        return;
      case 'payout_completed':
      case 'payout_submitted':
      case 'payout_approved':
      case 'payout_rejected':
        // User payout notifications - navigate to receipt page
        if (notification.link_id) {
          navigate(`/payout/${notification.link_id}`);
        } else {
          navigate('/earnings');
        }
        return;
      case 'ticket_checkin':
        // Navigate to the event page
        if (notification.link_id) {
          navigate(`/events/${notification.link_id}`);
        } else {
          navigate('/events/my-tickets');
        }
        return;
      case 'ticket_purchase':
      case 'ticket_sold':
        // Navigate to event or my tickets
        if (notification.link_id) {
          navigate(`/events/${notification.link_id}`);
        } else {
          navigate('/events/my-tickets');
        }
        return;
      case 'event_reminder':
        // Navigate to the event
        if (notification.link_id) {
          navigate(`/events/${notification.link_id}`);
        } else {
          navigate('/events');
        }
        return;
      case 'vip_leaderboard_granted':
      case 'vip_leaderboard_revoked':
      case 'vip_subscription':
        navigate('/vip');
        return;
      case 'blast_completed':
      case 'blast_scheduled':
        navigate('/email-blast');
        return;
      case 'kyc_approved':
      case 'kyc_rejected':
      case 'kyc_submitted':
        navigate('/settings');
        return;

      // ── Pass-B typed notifications (defensive fallback when `link` field absent) ──
      case 'revenue_milestone':
      case 'withdrawal_requested':
      case 'payout_sent':
      case 'first_sale_ever':
      case 'tax_form_ready':
        navigate('/dashboard?tab=earnings');
        return;
      case 'follower_milestone':
      case 'creator_inactive_nudge':
      case 'creator_weekly_digest':
      case 'platform_anniversary':
        navigate('/dashboard');
        return;
      case 'profile_incomplete':
      case 'email_changed':
      case 'twofa_toggled':
      case 'new_device_login':
      case 'password_changed':
      case 'stripe_setup_incomplete':
      case 'payment_failed':
      case 'subscription_renewal_upcoming':
        navigate('/settings');
        return;
      case 'trial_ending':
        navigate('/pricing');
        return;
      case 'abandoned_cart':
        navigate('/cart');
        return;
      case 'wishlist_back_in_stock':
      case 'new_merch_followed_artist':
      case 'merch_drop':
        navigate(notification.merch_id || linkId ? `/merch/item/${notification.merch_id || linkId}` : '/merch');
        return;
      case 'inventory_empty':
      case 'inventory_stale':
        navigate('/dashboard?tab=merch&subtab=store');
        return;
      case 'order_shipped_tracking':
      case 'order_delivery_delayed':
      case 'refund_processed':
        navigate('/dashboard?tab=merch&subtab=purchases');
        return;
      case 'price_drop':
      case 'new_release_followed_artist':
      case 'release_countdown':
      case 'featured_on_platform':
      case 'friend_purchase':
        // Use ctx-driven content_type/id when available
        if (notification.content_type && notification.content_id) {
          navigate(`/${notification.content_type}/${notification.content_id}`);
        } else {
          navigate('/home');
        }
        return;
      case 'comment_reply':
        if (notification.content_type && notification.content_id) {
          navigate(`/${notification.content_type}/${notification.content_id}?showComments=true`);
        } else {
          navigate('/home');
        }
        return;
      case 'concert_near_you':
      case 'event_near_you_followed_artist':
        navigate(notification.event_id ? `/events/${notification.event_id}` : '/events');
        return;
      case 'listening_party_replay':
        navigate(notification.party_id ? `/listening-parties/${notification.party_id}` : '/listening-parties');
        return;
      case 'booking_24h_reminder':
      case 'booking_1h_reminder':
      case 'booking_inquiry':
      case 'post_event_review_request':
      case 'rebook_request':
        navigate('/dj?tab=bookings');
        return;
      case 'fan_reactivation':
      case 'weekly_recommendations':
        navigate('/home');
        return;
    }
    
    // Handle content-based navigation
    switch (linkType) {
      case 'track':
        navigate(`/track/${linkId}`);
        break;
      case 'beat':
        navigate(`/beat/${linkId}`);
        break;
      case 'video':
        navigate(`/free-videos?video=${linkId}`);
        break;
      case 'playlist':
        navigate(`/playlist/share/${linkId}`);
        break;
      case 'profile':
        navigate(`/profile/${linkId}`);
        break;
      case 'user':
        navigate(`/profile/${notification.from_user_id || linkId}`);
        break;
      case 'review_session':
        if (notification.session_code) {
          navigate(`/review/${notification.session_code}`);
        } else {
          navigate('/music-review');
        }
        break;
      case 'admin_payouts':
        // Admin payouts link - navigate to admin payouts tab
        navigate('/admin?tab=payouts');
        break;
      case 'admin_ai_verification':
        // Admin AI verification - navigate to admin verifications tab
        navigate('/admin?tab=verifications');
        break;
      case 'verification':
        // User verification result - navigate to settings
        navigate('/settings');
        break;
      case 'payout_receipt':
        // Navigate to payout receipt page
        if (linkId) {
          navigate(`/payout/${linkId}`);
        } else {
          navigate('/earnings');
        }
        break;
      case 'earnings':
        // Navigate to earnings page
        navigate('/earnings');
        break;
      case 'event':
        // Navigate to event page
        navigate(`/events/${linkId}`);
        break;
      case 'gig':
      case 'gig_applications':
        // Navigate to gig/booking related page
        navigate(`/gigs/${linkId}`);
        break;
      case 'booking':
        // Navigate to bookings (DJ dashboard or promoter dashboard depending on role)
        navigate('/dj?tab=bookings');
        break;
      case 'promote_credit':
        // Paid-To-Listen launch bonus deep-link → opens Promote with the
        // credit pre-selected. Frontend Promote page reads ?credit_id=
        // and pre-applies the credit on the campaign step.
        navigate(linkId ? `/promote?credit_id=${linkId}` : '/promote');
        break;
      case 'ptl_payout_request':
        // Admin-only — full breakdown view.
        navigate(`/admin/paid-to-listen?request=${linkId}`);
        break;
      case 'ptl_payout_paid':
        // User-side — view earnings/balance.
        navigate('/paid-to-listen');
        break;
      default:
        // Fallback handling
        if (notification.content_type === 'track' && notification.content_id) {
          navigate(`/track/${notification.content_id}`);
        } else if (notification.content_type === 'beat' && notification.content_id) {
          navigate(`/beat/${notification.content_id}`);
        } else if (notification.content_type === 'video' && notification.content_id) {
          navigate(`/free-videos?video=${notification.content_id}`);
        } else if (notification.playlist_id) {
          navigate(`/playlist/share/${notification.playlist_id}`);
        } else if (notification.session_code) {
          navigate(`/review/${notification.session_code}`);
        } else if (notification.fan_id) {
          navigate(`/profile/${notification.fan_id}`);
        } else if (notification.from_user_id) {
          navigate(`/profile/${notification.from_user_id}`);
        } else {
          // Last-resort fallback: send the user to the home feed so a
          // notification click never silently no-ops.
          navigate('/home');
        }
    }
  };

  const getNotificationIcon = (notification) => {
    const notifType = notification.notification_type || notification.type;
    const iconClass = notification.read ? 'text-[#888888]' : 'text-[#00CCFF]';
    
    switch (notifType) {
      case 'playlist_like':
      case 'like':
      case 'video_like':
        return <Heart size={14} className={notification.read ? 'text-[#888888]' : 'text-[#ff69b4]'} />;
      case 'playlist_shared':
      case 'playlist_shared_by_other':
      case 'content_share':
        return <Share2 size={14} className={iconClass} />;
      case 'playlist_listen':
      case 'stream':
        return <ListMusic size={14} className={iconClass} />;
      case 'friend_activity':
        return <Users size={14} className={notification.read ? 'text-[#888888]' : 'text-[#FFDA33]'} />;
      case 'follow':
      case 'new_friend':
        return <User size={14} className={iconClass} />;
      case 'comment':
      case 'playlist_comment':
      case 'video_comment':
        return <MessageSquare size={14} className={iconClass} />;
      case 'mention':
        return <AtSign size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00CCFF]'} />;
      case 'purchase':
      case 'merch_sale':
        return <DollarSign size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00ff00]'} />;
      case 'donation':
        return <Heart size={14} className={notification.read ? 'text-[#888888]' : 'text-[#ff6b6b]'} />;
      case 'leaderboard_achievement':
        return <Trophy size={14} className={notification.read ? 'text-[#888888]' : 'text-[#FFDA33]'} />;
      case 'approval':
      case 'content_approved':
        return <ShieldCheck size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00ff00]'} />;
      case 'rejection':
      case 'content_rejected':
        return <ShieldCheck size={14} className={notification.read ? 'text-[#888888]' : 'text-[#ff0000]'} />;
      case 'upload':
      case 'new_content':
        return <Music size={14} className={notification.read ? 'text-[#888888]' : 'text-[#9b59b6]'} />;
      case 'ai':
      case 'ai_content':
        return <Bot size={14} className={notification.read ? 'text-[#888888]' : 'text-[#9b59b6]'} />;
      case 'message':
      case 'new_message':
        return <MessageSquare size={14} className={iconClass} />;
      case 'live_radio':
        return <Radio size={14} className={notification.read ? 'text-[#888888]' : 'text-[#ff0000]'} />;
      case 'livestream':
        return <Radio size={14} className={notification.read ? 'text-[#888888]' : 'text-[#ff0000]'} />;
      case 'video_submission':
        return <Video size={14} className={notification.read ? 'text-[#888888]' : 'text-[#9333ea]'} />;
      case 'new_payout_request':
      case 'payout_request':
        return <DollarSign size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00ff88]'} />;
      case 'new_ai_verification_request':
        return <FileCheck size={14} className={notification.read ? 'text-[#888888]' : 'text-[#FFDA33]'} />;
      case 'ai_verification_approved':
        return <ShieldCheck size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00ff88]'} />;
      case 'ai_verification_rejected':
        return <FileX size={14} className={notification.read ? 'text-[#888888]' : 'text-[#ff4444]'} />;
      case 'new_upload':
        return <Music size={14} className={notification.read ? 'text-[#888888]' : 'text-[#FFDA33]'} />;
      case 'referral_success':
        return <Users size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00ff88]'} />;
      case 'pre_save':
        return <Heart size={14} className={notification.read ? 'text-[#888888]' : 'text-[#FFDA33]'} />;
      case 'release_live':
        return <Music size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00CCFF]'} />;
      case 'ticket_checkin':
        return <Ticket size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00ff88]'} />;
      case 'ticket_purchase':
      case 'ticket_sold':
        return <Ticket size={14} className={notification.read ? 'text-[#888888]' : 'text-[#FFDA33]'} />;
      case 'event_reminder':
        return <Ticket size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00CCFF]'} />;
      case 'vip_leaderboard_granted':
      case 'vip_leaderboard_revoked':
        return <Trophy size={14} className={notification.read ? 'text-[#888888]' : 'text-[#FFDA33]'} />;
      case 'blast_completed':
      case 'blast_scheduled':
        return <Radio size={14} className={notification.read ? 'text-[#888888]' : 'text-[#00CCFF]'} />;
      default:
        return notification.content_type === 'video' 
          ? <Video size={14} className={iconClass} />
          : <Music size={14} className={iconClass} />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <DropdownMenu onOpenChange={(open) => open && fetchNotifications()}>
      <DropdownMenuTrigger asChild>
        <button 
          className="header-icon-glow relative p-2 rounded-lg transition-all"
          data-testid="notification-bell"
          style={{
            '--header-icon-bg': '#FFDA33',
            '--header-icon-bg-2': '#d99e00',
            '--header-icon-glow': 'rgba(255, 218, 51, 0.55)',
            '--header-icon-glow-2': 'rgba(255, 218, 51, 0.25)',
          }}
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#00CCFF] text-black text-xs font-mono flex items-center justify-center rounded-full ring-2 ring-[#0a0a0a]">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-[#0a0a0a] border-[#1a1a1a] rounded-none"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#1a1a1a]">
          <span className="text-white font-['Chakra_Petch'] uppercase text-sm">Notifications</span>
          {unreadCount > 0 && (
            <button 
              onClick={markAllAsRead}
              className="text-[#00CCFF] text-xs font-mono hover:text-[#00c8ff] flex items-center gap-1"
            >
              <CheckCheck size={12} /> Mark all read
            </button>
          )}
        </div>
        
        {loading ? (
          <div className="p-4 text-center text-[#888888] font-mono text-sm">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center text-[#888888] font-mono text-sm">No notifications</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {notifications.map(notification => (
              <DropdownMenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`px-3 py-3 cursor-pointer rounded-none border-b border-[#1a1a1a] last:border-b-0 ${
                  !notification.read ? 'bg-[#111111]' : ''
                }`}
              >
                <div className="flex items-start gap-3 w-full">
                  <div className={`p-2 ${notification.read ? 'bg-[#1a1a1a]' : 'bg-[#00CCFF]/20'}`}>
                    {getNotificationIcon(notification)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-mono ${notification.read ? 'text-[#888888]' : 'text-white'}`}>
                      {notification.body || notification.message || notification.title}
                    </p>
                    <p className="text-xs text-[#666666] font-mono mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-[#00CCFF] rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
